import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import '../styles/Login.css';

// Development-only login component for testing
// This bypasses Google authentication and allows login as any existing user

function DevLogin({ onDevLogin }) {
  const [selectedUser, setSelectedUser] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      setFetchingUsers(true);
      const snapshot = await getDocs(collection(db, 'users'));
      const usersList = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      
      // Sort users by role then by name
      usersList.sort((a, b) => {
        const roleOrder = { admin: 1, loaner: 2, loanee: 3, new_user: 4 };
        const roleCompare = (roleOrder[a.role] || 5) - (roleOrder[b.role] || 5);
        if (roleCompare !== 0) return roleCompare;
        return (a.displayName || '').localeCompare(b.displayName || '');
      });
      
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setFetchingUsers(false);
    }
  }

  async function handleDevLogin() {
    if (!selectedUser) return;

    try {
      setLoading(true);
      const user = users.find(u => u.uid === selectedUser);

      if (!user) {
        alert('User not found');
        return;
      }

      console.log('Attempting dev login for:', user);

      // Create user object with required fields
      const loginUser = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}&background=0572CE&color=fff`,
        role: user.role
      };

      // Simulate login by passing user data to parent
      await onDevLogin(loginUser);
      
      console.log('Dev login successful, navigating to dashboard');
      navigate('/dashboard');
    } catch (error) {
      console.error('Dev login error:', error);
      console.error('Error details:', error.message, error.code);
      alert(`Failed to login as user: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  // Group users by role for better display
  const adminUsers = users.filter(u => u.role === 'admin');
  const loanerUsers = users.filter(u => u.role === 'loaner');
  const loaneeUsers = users.filter(u => u.role === 'loanee');
  const newUsers = users.filter(u => u.role === 'new_user');

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>🧪 Development Mode</h1>
          <p className="login-subtitle">Select a user to login as</p>
        </div>

        {fetchingUsers ? (
          <div className="loading">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <p>No users found in the database.</p>
            <p style={{ fontSize: '12px', marginTop: '8px' }}>
              Please create users through Google authentication first.
            </p>
          </div>
        ) : (
          <>
            <div className="dev-user-select">
              <label htmlFor="test-user">Select User:</label>
              <select
                id="test-user"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="select-field"
                style={{ width: '100%', marginTop: '8px' }}
              >
                <option value="">-- Select a user --</option>
                
                {adminUsers.length > 0 && (
                  <optgroup label="Admins">
                    {adminUsers.map(user => (
                      <option key={user.uid} value={user.uid}>
                        {user.displayName || user.email} ({user.role})
                      </option>
                    ))}
                  </optgroup>
                )}
                
                {loanerUsers.length > 0 && (
                  <optgroup label="Loaners">
                    {loanerUsers.map(user => (
                      <option key={user.uid} value={user.uid}>
                        {user.displayName || user.email} ({user.role})
                      </option>
                    ))}
                  </optgroup>
                )}
                
                {loaneeUsers.length > 0 && (
                  <optgroup label="Loanees">
                    {loaneeUsers.map(user => (
                      <option key={user.uid} value={user.uid}>
                        {user.displayName || user.email} ({user.role})
                      </option>
                    ))}
                  </optgroup>
                )}
                
                {newUsers.length > 0 && (
                  <optgroup label="New Users (Pending Approval)">
                    {newUsers.map(user => (
                      <option key={user.uid} value={user.uid}>
                        {user.displayName || user.email} ({user.role})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleDevLogin}
              disabled={!selectedUser || loading}
              style={{ width: '100%', marginTop: '24px' }}
            >
              {loading ? 'Logging in...' : 'Login as Selected User'}
            </button>
          </>
        )}

        <div className="login-footer" style={{ marginTop: '24px' }}>
          <p style={{ color: '#FF6C00', fontWeight: 'bold' }}>
            ⚠️ Development Mode Only
          </p>
          <p style={{ fontSize: '12px', marginTop: '8px' }}>
            This bypasses Google authentication for testing purposes.
            Shows actual users from the database.
          </p>
        </div>
      </div>
    </div>
  );
}

export default DevLogin;
